import { useEffect, useCallback, useState } from 'react'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import { Breadcrumbs, Link } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import LoadingFullPage from 'components/LoadingFullPage'
import Select from 'components/Select'
import { QUERY_KEYS } from 'constants/keys'
import { ROUTES } from 'constants/routes'
import { useGlobalState } from 'contexts/global-state'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { ApiService } from 'services/api'

import Delete from './delete'

type AddProps = {
  activityTask?: ActivityTask
  onSave?: (activityTasks: ActivityTask) => Promise<void>
  isEditMode?: boolean
}

export default function Add({
  activityTask,
  onSave,
  isEditMode = false,
}: AddProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { openSuccessToast, openErrorToast } = useGlobalState()

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
    control,
    watch,
  } = useForm<ActivityTask>()

  const [formUpdated, setFormUpdated] = useState<boolean>(false)

  const [deleteConfirmation, setDeleteConfirmation] = useState<
    ActivityTask | undefined
  >()

  useEffect(() => {
    if (isEditMode && !!activityTask && !formUpdated) {
      reset({
        name: activityTask.name,
      })
      setValue('default', activityTask.default)
      setValue('active', activityTask.active)
      setFormUpdated(true)
    }
  }, [isEditMode, activityTask, formUpdated, reset, setValue])

  const inputProps = useCallback(
    (fieldName: keyof ActivityTask) => {
      if (isEditMode) return { InputLabelProps: { shrink: true } }

      return { InputLabelProps: { shrink: !!watch(fieldName) } }
    },
    [isEditMode, watch],
  )

  const addNewActivityTask = useMutation({
    mutationFn: async (activityTask: ActivityTask) => {
      await ApiService.ActivityTasks.create({
        name: activityTask.name,
        default: !!Number(activityTask.default),
        active: isEditMode ? !!Number(activityTask.active) : true,
      })
    },
    onSuccess: (_data, activityTask: ActivityTask) => {
      queryClient.setQueryData<ActivityTask[]>(
        QUERY_KEYS.ACTIVITY_TASKS.LIST,
        (oldActivityTask) => {
          if (!oldActivityTask?.length) return

          return oldActivityTask.map((currentActivityTask) => {
            if (currentActivityTask.id === activityTask.id) {
              return activityTask
            }

            return currentActivityTask
          })
        },
      )

      openSuccessToast({
        message: `A tarefa ${activityTask.name} foi adicionado com sucesso!`,
      })

      navigate(ROUTES.ACTIVITY_TASKS.LIST)
    },
    onError: (error, activityTask: ActivityTask) => {
      const apiError = error as ApiError

      if (apiError?.status === 400) {
        openErrorToast({
          message: `A tarefa ${activityTask.name} já está cadastrado!`,
        })

        return
      }

      openErrorToast({
        message: `Erro ao adicionar ${activityTask.name}!`,
      })
    },
  })

  if (isEditMode && !!activityTask && formUpdated === false) {
    return <LoadingFullPage />
  }

  return (
    <>
      <Box component="main" sx={{ flexGrow: 1, py: 8 }}>
        <Container maxWidth={false}>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator={<NavigateNextIcon fontSize="small" />}
          >
            <Link
              underline="hover"
              color="inherit"
              href={ROUTES.ACTIVITY_TASKS.LIST}
            >
              Tarefas
            </Link>

            <Typography color="text.primary">
              {isEditMode ? 'Editar' : 'Adicionar'}
            </Typography>
          </Breadcrumbs>
          <Box>
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ArrowBackIcon
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    navigate(-1)
                  }}
                />

                <Typography sx={{ m: 1 }} variant="h4">
                  {isEditMode ? 'Editar' : 'Adicionar'} Tarefas
                </Typography>
              </Box>

              <Box sx={{ m: 1, display: 'flex' }}>
                {isEditMode && (
                  <Box sx={{ m: 1 }}>
                    <Button
                      color="error"
                      variant="contained"
                      onClick={() => {
                        setDeleteConfirmation(activityTask as ActivityTask)
                      }}
                    >
                      Remover
                    </Button>
                  </Box>
                )}

                <Box sx={{ m: 1 }}>
                  <Button
                    color="info"
                    variant="contained"
                    onClick={() => {
                      navigate(ROUTES.ACTIVITY_TASKS.LIST)
                    }}
                  >
                    Listar
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box sx={{ mt: 3 }}>
            <form
              onSubmit={handleSubmit((activityTask: ActivityTask) => {
                if (isEditMode) {
                  onSave?.(activityTask)
                  return
                }

                addNewActivityTask.mutate(activityTask)
              })}
            >
              <Card>
                <CardContent>
                  <Grid item container spacing={3} xs={12}>
                    <Grid item xs={6}>
                      <TextField
                        error={!!errors.name?.message}
                        fullWidth
                        helperText={errors.name?.message}
                        label="Nome"
                        margin="normal"
                        type="text"
                        variant="outlined"
                        {...inputProps('name')}
                        {...register('name')}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <Controller
                        name="default"
                        control={control}
                        render={({ field: { onChange, value } }) => {
                          return (
                            <Select
                              label="Padrão"
                              id="default"
                              onChange={onChange}
                              value={value?.toString()}
                              {...inputProps}
                              autoFill={isEditMode}
                              options={[
                                {
                                  label: 'Sim',
                                  value: '1',
                                },
                                {
                                  label: 'Não',
                                  value: '0',
                                },
                              ]}
                            />
                          )
                        }}
                      />
                    </Grid>
                    {isEditMode && (
                      <Grid item xs={3}>
                        <Controller
                          name="active"
                          control={control}
                          render={({ field: { onChange, value } }) => {
                            return (
                              <Select
                                label="Ativo"
                                id="active"
                                onChange={onChange}
                                value={value?.toString()}
                                {...inputProps}
                                autoFill={isEditMode}
                                options={[
                                  {
                                    label: 'Sim',
                                    value: '1',
                                  },
                                  {
                                    label: 'Não',
                                    value: '0',
                                  },
                                ]}
                              />
                            )
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>

                  <Box>
                    <Button
                      type="submit"
                      color="primary"
                      variant="contained"
                      style={{
                        marginLeft: 'auto',
                        display: 'block',
                        marginTop: '1em',
                      }}
                    >
                      {isEditMode ? 'Salvar' : 'Adicionar'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </form>
          </Box>
        </Container>
      </Box>

      <Delete
        opened={!!deleteConfirmation}
        closeModal={() => setDeleteConfirmation(undefined)}
        activityTask={deleteConfirmation as ActivityTask}
      />
    </>
  )
}
